<script setup>
// X216 (COUNTRY_PLAN.md §4.2, Grant 2026-10-28: "when they next log in", on ANY page): mounted once in app.vue. After
// the launch refresh of /auth/me it opens ONE dialog for every club this admin runs that has no country
// (user.clubsNeedingCountry, computed by the API per request and never under impersonation). It waits for the other
// walls and prompts (two-step, account setup, a form prompt, the user tag step, the goals or two-step offer dialogs),
// and never opens on the sign-in, prompt, two-step, TV, embed or bare pages, nor on any invite / join / claim page.
import {computed, onMounted, ref, watch} from 'vue';
import {useRoute} from 'vue-router';
import ClubCountryDialog from '~/components/clubs/ClubCountryDialog.vue';
import {useAuthService} from '~/composables/useAuthService';
import {usePromptSlots} from '~/composables/usePromptSlots';
import {nextLandingSkipPath, shouldAskCountry} from '~/utils/clubCountry';

const route = useRoute();
const {user, isAuthenticated, userRefreshed, updateLocalUser} = useAuthService();
const slots = usePromptSlots();

const open = ref(false);
const mounted = ref(false);
// Answered or put off in this tab: never reopened from a stale user copy before the refresh lands.
const dismissed = ref(false);
const clubs = ref([]);
// X222 (R91-M1): a page entered from an e-mail link stays skipped until the visitor goes to a different path, even
// after it tidies its own address (?t=, ?pay=). Decided from the first route, updated on every route change.
let landingSkipPath = nextLandingSkipPath(null, route.path, route.query);
watch(() => route.fullPath, () => { landingSkipPath = nextLandingSkipPath(landingSkipPath, route.path, route.query); }, {flush: 'sync'});
slots.hold('clubCountry', open);

const check = () => {
  if (!mounted.value || open.value || !isAuthenticated.value) return;
  const ask = shouldAskCountry({
    user: user.value,
    refreshed: userRefreshed.value,
    path: route.path,
    query: route.query,
    layout: route.meta?.layout ?? null,
    otherPromptOpen: slots.othersOpen('clubCountry'),
    dismissed: dismissed.value,
    landingSkipPath,
  });
  if (!ask) return;
  clubs.value = [...user.value.clubsNeedingCountry];
  open.value = true;
};

let timer = null;
const later = (ms) => {
  if (timer) clearTimeout(timer);
  timer = setTimeout(check, ms);
};

// After the other prompts have had their turn to open (they check at 600-1200 ms).
onMounted(() => { mounted.value = true; later(1800); });
const clubKey = computed(() => (user.value?.clubsNeedingCountry ?? []).map((c) => c.clubId).join(','));
watch(() => [user.value?.id, clubKey.value, userRefreshed.value, route.fullPath, Object.keys(slots.open.value).length], () => later(900));
// A new sign-in may ask again.
watch(() => user.value?.id, () => { dismissed.value = false; });

const done = async () => {
  dismissed.value = true;
  // The next /auth/me no longer lists what was answered or put off.
  try { await updateLocalUser(); } catch { /* the stale copy is held off by `dismissed` */ }
};
</script>

<template>
  <club-country-dialog v-if="isAuthenticated" v-model="open" :clubs="clubs" @saved="done" @snoozed="done"/>
</template>
